<template>
    <span ref="triggerRef" v-click-outside="close"
          :class="{
                'select2 select2-container select2-container--default select2-container--above': true,
                'select2-container--focus select2-container--open': isOpen,
                'select2-container--disabled': isDisabled
          }"
          :style="isDisabled ? { cursor: 'not-allowed' } : {}"
          @click="toggle">
        <span class="selection">
            <span class="select2-selection select2-selection--single">
                <span class="select2-selection__rendered">
                    <span v-if="modelValue.length && !isDisabled" class="select2-selection__clear" @click.stop="$emit('update:modelValue', [])">×</span>
                    <span v-if="modelValue.length">{{ selectedText }}</span>
                    <span v-else class="select2-selection__placeholder">Пусто</span>
                </span>
                <span class="select2-selection__arrow" role="presentation">
                    <b role="presentation"></b>
                </span>
            </span>
        </span>
        <span class="dropdown-wrapper" aria-hidden="true"></span>
    </span>

    <Teleport to="body">
        <div
            v-if="isOpen"
            class="multiselect-container dropdown-menu show"
            ref="menuRef"
            :style="{ display: 'flex', flexDirection: 'column', width: menuWidth, overflow: 'hidden', zIndex: 9999, minHeight: '302px' }"
            @click.stop
        >
            <div class="multiselect-item multiselect-filter mb-0">
                <div class="input-group">
                    <input class="form-control form-control-sm multiselect-search" type="text" placeholder="Search" v-model="search" @input="onSearchInput" ref="searchInputRef">
                    <i class="icon-search4"></i>
                    <span v-if="canCreateIntegrationItems" class="input-group-append">
                        <button class="btn bg-teal btn-icon btn-sm" type="button" :disabled="!search || createButtonIsDisabled" @click="createInternalItem">
                            <i class="icon-plus2"></i>
                        </button>
                    </span>
                </div>
            </div>
            <div style="display: flex; flex: 1; overflow: hidden;">
                <div :style="{ display: 'flex', flexDirection: 'column', width: hasMarketPlaceItems ? '50%' : '100%', 'border-right': hasMarketPlaceItems ? '1px solid #e9ecef' : 'none' }">
                    <div class="multiselect-item multiselect-group">
                        <label>{{ $t('labels.integrations_attribute_items') }}</label>
                    </div>
                    <div ref="internalListRef" class="items-scroll-container">
                        <div v-for="item in internalItems" :key="item.uuid" class="multiselect-item dropdown-item form-check" :class="{ active: isSelected(item) }" :aria-selected="isSelected(item)">
                            <label class="form-check-label" @click.prevent="toggleItem(item)">
                                <input type="checkbox" :checked="isSelected(item)" @click.stop="toggleItem(item)"> {{ item.name }}
                                <span class="form-check-control-indicator"></span>
                            </label>
                        </div>
                    </div>
                </div>
                <div v-if="hasMarketPlaceItems" style="display: flex; flex-direction: column; width: 50%;">
                    <div class="multiselect-item multiselect-group">
                        <label>{{ $t('labels.marketplace') }}</label>
                    </div>
                    <div v-if="messageFromMarketPlaceError" class="p-2">
                        <Alert type="warning" :dismissible="false" margin-bottom="mb-0">
                            {{ messageFromMarketPlaceError }}
                        </Alert>
                    </div>
                    <div v-else ref="externalListRef" class="items-scroll-container">
                        <div
                            v-for="item in marketplaceItems"
                            :key="item.uuid"
                            class="multiselect-item dropdown-item"
                            :class="{ disabled: isMarketplaceItemConnected(item) }"
                            :style="{ cursor: isMarketplaceItemConnected(item) ? 'default' : 'pointer', opacity: isMarketplaceItemConnected(item) ? 0.5 : 1 }"
                            @click="!isMarketplaceItemConnected(item) && importMarketplaceItem(item)"
                        >
                            <i class="icon-arrow-left8 text-muted"></i> {{ item.name_uk }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>
<script>
import { createPopper } from '@popperjs/core';
import vClickOutside from 'click-outside-vue3';
import { httpClient } from '../../../../../../services/HttpClient';
import { getMessageFromMarketPlaceError } from '../../../../../../utils';
import Alert from '../../../../../../components/AlertComponent.vue';

export default {
    name: "CatalogAttributeItemMultiAutocomplete",
    components: {
        Alert
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        attribute: {
            type: Object,
            required: true,
        },
        integration: {
            type: Object,
            required: true,
        },
        category: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isOpen: false,
            popperInstance: null,
            search: '',
            searchDebounceTimer: null,
            internalItems: [],
            internalPage: 0,
            internalTotal: null,
            isFetchingInternal: false,
            marketplaceItems: [],
            marketplacePage: 0,
            marketplaceTotal: null,
            isFetchingMarketplace: false,
            messageFromMarketPlaceError: null,
            menuWidth: '250px',
            createButtonIsDisabled: false,
        };
    },
    computed: {
        hasMarketPlaceItems() {
            return !!this.category.source_id && !!this.attribute.source_id;
        },
        canCreateIntegrationItems() {
            return this.attribute.allow_custom_values;
        },
        isDisabled() {
            return Array.isArray(this.attribute.default_items) && this.attribute.default_items.length > 0;
        },
        marketplaceItemsUrl() {
            return `/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integration.service}/categories/${this.category.source_id}/attributes/${this.attribute.source_id}/items`;
        },
        internalSourceIds() {
            return this.internalItems.map(item => item.source_id).filter(Boolean);
        },
        selectedText() {
            return this.modelValue.map(item => item.name).join(', ');
        },
    },
    methods: {
        isSelected(item) {
            return this.modelValue.some(selected => selected.uuid === item.uuid);
        },
        isMarketplaceItemConnected(item) {
            return this.internalSourceIds.includes(item.source_id);
        },
        toggleItem(item) {
            const newValue = this.isSelected(item)
                ? this.modelValue.filter(selected => selected.uuid !== item.uuid)
                : [...this.modelValue, item];
            this.$emit('update:modelValue', newValue);
        },
        toggle() {
            if (this.isDisabled) {
                return;
            }
            this.isOpen ? this.close() : this.open();
        },
        async open() {
            this.isOpen = true;
            this.$nextTick(() => {
                this.$refs.searchInputRef.focus({ preventScroll: true });
                const triggerWidth = this.$refs.triggerRef?.offsetWidth ?? 0;
                this.menuWidth = Math.max(this.hasMarketPlaceItems ? 500 : 250, triggerWidth) + 'px';

                this.popperInstance = createPopper(this.$refs.triggerRef, this.$refs.menuRef, {
                    placement: 'bottom-start',
                    modifiers: [
                        { name: 'offset', options: { offset: [0, 5] } },
                    ],
                });

                if (this.$refs.internalListRef) {
                    this.$refs.internalListRef.addEventListener('scroll', this.onInternalScroll);
                }
                if (this.$refs.externalListRef) {
                    this.$refs.externalListRef.addEventListener('scroll', this.onExternalScroll);
                }
            });

            await this.fetchInternalItems();

            if (this.hasMarketPlaceItems) {
                this.fetchMarketplaceItems();
            }
        },
        close() {
            this.isOpen = false;
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
            if (this.$refs.internalListRef) {
                this.$refs.internalListRef.removeEventListener('scroll', this.onInternalScroll);
            }
            if (this.$refs.externalListRef) {
                this.$refs.externalListRef.removeEventListener('scroll', this.onExternalScroll);
            }
            this.search = '';
            this.internalItems = [];
            this.internalPage = 0;
            this.internalTotal = null;
            this.marketplaceItems = [];
            this.marketplacePage = 0;
            this.marketplaceTotal = null;
            this.messageFromMarketPlaceError = null;
        },
        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.internalItems = [];
                this.internalPage = 0;
                this.internalTotal = null;
                this.fetchInternalItems();

                if (this.hasMarketPlaceItems) {
                    this.marketplaceItems = [];
                    this.marketplacePage = 0;
                    this.marketplaceTotal = null;
                    this.fetchMarketplaceItems();
                }
            }, 400);
        },
        onInternalScroll(event) {
            const el = event.target;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
            if (atBottom) {
                this.fetchInternalItems();
            }
        },
        onExternalScroll(event) {
            const el = event.target;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
            if (atBottom) {
                this.fetchMarketplaceItems();
            }
        },
        async createInternalItem() {
            this.createButtonIsDisabled = true;
            await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                names: { uk: this.search, ru: this.search },
                integration_filter_uuid: this.attribute.uuid,
            });

            this.internalItems = [];
            this.internalPage = 0;
            this.internalTotal = null;
            await this.fetchInternalItems();

            const created = this.internalItems.find(i => i.name === this.search);
            if (created && !this.isSelected(created)) {
                this.$emit('update:modelValue', [...this.modelValue, created]);
            }

            this.search = '';
            this.createButtonIsDisabled = false;

            this.internalItems = [];
            this.internalPage = 0;
            this.internalTotal = null;
            this.fetchInternalItems();
        },
        async importMarketplaceItem(item) {
            const names = {};
            if (item.name_uk !== null && item.name_uk !== '') names.uk = item.name_uk;
            if (item.name_ru !== null && item.name_ru !== '') names.ru = item.name_ru;

            let request = {
                integration_filter_uuid: this.attribute.uuid,
                source_id: item.source_id,
                names,
            };

            await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, request);

            this.internalItems = [];
            this.internalPage = 0;
            this.internalTotal = null;
            await this.fetchInternalItems();

            const created = this.internalItems.find(i => i.source_id === item.source_id);
            if (created && !this.isSelected(created)) {
                this.$emit('update:modelValue', [...this.modelValue, created]);
            }
        },
        async fetchInternalItems() {
            if (this.internalItems.length === this.internalTotal || this.isFetchingInternal) {
                return;
            }

            this.isFetchingInternal = true;
            this.internalPage++;

            const params = {
                integration_filter_uuids: [this.attribute.uuid],
                limit: 20,
                page: this.internalPage,
            };

            if (this.search) {
                params.search = this.search;
            }

            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, params);

            this.internalTotal = response.data.meta.total;
            this.internalItems.push(...response.data.data);
            this.isFetchingInternal = false;
        },
        async fetchMarketplaceItems() {
            if (this.marketplaceItems.length === this.marketplaceTotal || this.isFetchingMarketplace || this.messageFromMarketPlaceError) {
                return;
            }

            this.isFetchingMarketplace = true;
            this.marketplacePage++;

            const params = {
                page: this.marketplacePage,
                limit: 20,
            };

            if (this.search) {
                params.search = this.search;
            }

            try {
                // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                const response = await httpClient.get(this.marketplaceItemsUrl, params, false, undefined, { skipNotFoundRedirect: true });

                this.marketplaceTotal = response.data.meta.total;
                this.marketplaceItems.push(...response.data.data);
            } catch (error) {
                const isUnsupportedAttribute = error.response?.status === 404 && error.response.data.message?.includes('MarketplaceAttribute');
                const messageFromMarketPlaceError = isUnsupportedAttribute
                    ? this.$t('alerts.marketplace_attribute_not_supported')
                    : getMessageFromMarketPlaceError(error);

                if (messageFromMarketPlaceError) {
                    this.messageFromMarketPlaceError = messageFromMarketPlaceError;
                    return;
                }
                
                throw error;
            } finally {
                this.isFetchingMarketplace = false;
            }
        },
    },
    beforeUnmount() {
        this.close();
    },
}
</script>

<style scoped>
.multiselect-item.multiselect-group {
    background-color: #f8f9fa;
}

.items-scroll-container {
    flex: 1;
    overflow-y: auto;
}

.multiselect.btn {
    display: flex;
    align-items: center;
    overflow: hidden;
}

.select2 {
    width: 100%;
}
</style>
